import React, { useMemo, useState } from 'react';
import { Users, Gift, ArrowRight, Sparkles, Info } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend, Treemap
} from 'recharts';

// Demo: RFM Segmentation + Lifecycle Plays
// - Dummy customer data with Recency (days), Frequency, Monetary (₺)
// - KMeans-like bucketing into segments (Very High/High/Med/Low)
// - Plays: Winback, VIP Upsell, First→Second Order, Reactivation, Cross-sell tip

const COLORS = ['#22c55e','#3b82f6','#f59e0b','#ef4444','#a855f7','#10b981'];
const SEGMENT_COLORS = {
  'CHAMPIONS': '#6366f1',           // indigo
  'LOYAL CUSTOMERS': '#a855f7',     // violet
  'POTENTIAL LOYALISTS': '#10b981', // emerald
  'NEW CUSTOMERS': '#22d3ee',       // cyan
  'PROMISING': '#f59e0b',           // amber
  'ABOUT TO SLEEP': '#14b8a6',      // teal
  'NEEDS ATTENTION': '#facc15',     // yellow
  'AT RISK': '#f97316',             // orange
  "CAN'T LOSE THEM": '#f43f5e',     // rose
  'HIBERNATING': '#64748b'          // slate
};

function seedRand(seed){ let t=seed|0; return ()=>{ t^=t<<13; t^=t>>17; t^=t<<5; return Math.abs(t)/0x7fffffff; }; }

function genCustomers(n=1200){
  const rnd = seedRand(424242);
  const arr = [];
  for(let i=0;i<n;i++){
    const seg = rnd();
    // craft some clusters
    let rec = Math.round( seg<0.25 ? 15 + rnd()*20 : seg<0.6 ? 45 + rnd()*60 : 110 + rnd()*120 ); // days since last
    let freq = Math.round( seg<0.2 ? 8+ rnd()*6 : seg<0.6 ? 3+ rnd()*3 : 1+ rnd()*1 );
    let monet = Math.round( seg<0.2 ? 6000 + rnd()*8000 : seg<0.6 ? 1200 + rnd()*2000 : 200 + rnd()*600 );
    const channel = ['Email','Push','SMS','Meta','Google','Direct'][Math.floor(rnd()*6)];
    arr.push({ id:i+1, recency:rec, frequency:freq, monetary:monet, channel });
  }
  return arr;
}

function bucketRFM(c){
  const r = c.recency<=30?3: c.recency<=90?2:1;
  const f = c.frequency>=6?3: c.frequency>=3?2:1;
  const m = c.monetary>=4000?3: c.monetary>=1500?2:1;
  const score = `${r}${f}${m}`;
  let segment = 'HIBERNATING';
  if (r===3 && f===3) segment = 'CHAMPIONS';
  else if (r===3 && f===2) segment = 'LOYAL CUSTOMERS';
  else if (r===3 && f===1) segment = (m>=2 ? 'PROMISING' : 'NEW CUSTOMERS');
  else if (r===2 && f===3) segment = 'POTENTIAL LOYALISTS';
  else if (r===2 && f===2) segment = 'NEEDS ATTENTION';
  else if (r===2 && f===1) segment = 'ABOUT TO SLEEP';
  else if (r===1 && f===3) segment = "CAN'T LOSE THEM";
  else if (r===1 && f===2) segment = 'AT RISK';
  else if (r===1 && f===1) segment = 'HIBERNATING';
  return { ...c, r, f, m, score, segment };
}

function playsForSegment(seg){
  switch(seg){
    case 'CHAMPIONS': return ['VIP upsell bundle','Early access drop','High‑value cross‑sell'];
    case 'LOYAL CUSTOMERS': return ['Subscription nudge','Referral code','Tiered loyalty bonus'];
    case 'NEW CUSTOMERS': return ['First→Second order coupon','Onboarding tips','Low AOV bundle'];
    case 'PROMISING': return ['Personalized cross‑sell','Free shipping threshold','Price‑drop alert'];
    case 'POTENTIAL LOYALISTS': return ['Loyalty teaser','Points booster','Post‑purchase add‑on'];
    case 'NEEDS ATTENTION': return ['Back‑in‑stock alert','Price drop reminder','Email nurture'];
    case 'ABOUT TO SLEEP': return ['Re‑engagement series','Soft incentive','Content drip'];
    case 'AT RISK': return ['Winback discount','Cart/browse reminders','Service recovery'];
    case "CAN'T LOSE THEM": return ['Priority support','Save plan offer','Personal outreach'];
    case 'HIBERNATING': default: return ['Reactivation email','Browse abandonment','Survey incentive'];
  }
}

export default function RfmLifecycleDemo(){
  const [customers] = useState(()=>genCustomers());
  const data = useMemo(()=>customers.map(bucketRFM),[customers]);

  const bySeg = useMemo(()=>{
    const map={};
    data.forEach(d=>{
      if(!map[d.segment]) map[d.segment]={ segment:d.segment, customers:0, revenue:0, avgAOV:0 };
      map[d.segment].customers+=1;
      map[d.segment].revenue+=d.monetary;
    });
    Object.values(map).forEach(x=>{ x.avgAOV = Math.round(x.revenue/Math.max(1,x.customers)); });
    return Object.values(map).sort((a,b)=>b.revenue-a.revenue);
  },[data]);

  const channelPref = useMemo(()=>{
    const map={};
    data.forEach(d=>{ map[d.channel]=(map[d.channel]||0)+1; });
    return Object.entries(map).map(([name,value])=>({name,value}));
  },[data]);

  const totalCustomers = data.length;
  const treemapData = useMemo(()=>{
    const stats = {};
    data.forEach(d=>{
      const k = d.segment;
      if(!stats[k]) stats[k] = { name:k, value:0, customers:0, revenue:0, fill: SEGMENT_COLORS[k]||'#e5e7eb' };
      stats[k].value += 1;
      stats[k].customers += 1;
      stats[k].revenue += d.monetary;
    });
    // Only include segments that actually have customers to avoid tiny filler tiles
    return Object.values(stats).filter(s => s.value > 0);
  },[data]);

  const TreemapTile = (props) => {
    if (!props) return null;
    const {
      x = 0,
      y = 0,
      width = 0,
      height = 0,
      name = '',
      value = 0,
      fill
    } = props;
    const padding = 8;
    const gap = 10; // visual gutter between tiles
    const gx = x + gap / 2;
    const gy = y + gap / 2;
    const gw = Math.max(0, width - gap);
    const gh = Math.max(0, height - gap);
    const customers = Number(value) || 0;
    const pct = totalCustomers ? Math.round((customers / totalCustomers) * 100) : 0;
    // If the tile is very small, draw only background to avoid text overflow
    const tiny = gw < 120 || gh < 50;
    const bg = SEGMENT_COLORS[name] || fill || '#0ea5e9';
    return (
      <g>
        <rect x={gx} y={gy} width={gw} height={gh} rx={12} ry={12} fill={bg} opacity={0.95} />
        {!tiny && (
          <>
            <text x={gx + padding} y={gy + 20} fill="#ffffff" fontSize="13" fontWeight="700" style={{textShadow:'0 1px 2px rgba(0,0,0,0.3)'}}>{name}</text>
            <text x={gx + padding} y={gy + 40} fill="#ffffff" fontSize="12" style={{textShadow:'0 1px 2px rgba(0,0,0,0.25)'}}>
              {Number(customers).toLocaleString('en-US')} ({pct}%)
            </text>
          </>
        )}
      </g>
    );
  };

  return (
    <div className="p-6 space-y-8">
      <div className="relative overflow-hidden bg-white p-8 rounded-3xl shadow-sm border border-gray-200">
        <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-gradient-to-br from-indigo-200 to-purple-200 blur-3xl opacity-40 pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-gradient-to-br from-emerald-200 to-cyan-200 blur-3xl opacity-40 pointer-events-none" />
        <div className="relative z-10 flex items-center gap-4">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center shadow-md">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-gray-900">RFM Segmentation</h1>
            <p className="text-sm text-gray-500 mt-1">Elegant lifecycle insights and action‑ready playbooks</p>
          </div>
        </div>
      </div>

      {/* Modern RFM Treemap */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-800 text-sm">RFM Matrix (Treemap)</h3>
          <div className="text-xs text-gray-500 flex items-center"><Info className="w-3.5 h-3.5 mr-1" />Area = customers, color = segment</div>
        </div>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={treemapData}
              dataKey="value"
              nameKey="name"
              stroke="none"
              fill="transparent"
              isAnimationActive={false}
              content={<TreemapTile />}
            />
          </ResponsiveContainer>
        </div>
      </div>

      {/* Minimal KPI strip */}
      {(() => {
        const segMap = {};
        bySeg.forEach(s => { segMap[s.segment] = s; });
        const kpis = [
          { label: 'Total Customers', value: data.length.toLocaleString('en-US') },
          { label: 'Champions', value: (segMap['CHAMPIONS']?.customers || 0).toLocaleString('en-US'), color: 'from-indigo-500 to-blue-500' },
          { label: 'Potential Loyalists', value: (segMap['POTENTIAL LOYALISTS']?.customers || 0).toLocaleString('en-US'), color: 'from-emerald-500 to-teal-500' },
          { label: 'At Risk', value: (segMap['AT RISK']?.customers || 0).toLocaleString('en-US'), color: 'from-orange-500 to-rose-500' },
          { label: 'Hibernating', value: (segMap['HIBERNATING']?.customers || 0).toLocaleString('en-US'), color: 'from-slate-600 to-slate-400' },
        ];
        return (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {kpis.map((k, idx) => (
              <div key={idx} className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-4">
                {k.color ? <div className={`absolute -top-10 -right-10 h-24 w-24 rounded-full bg-gradient-to-br ${k.color} opacity-20 blur-2xl`} /> : null}
                <div className="relative z-10">
                  <div className="text-xs text-gray-500">{k.label}</div>
                  <div className="text-xl font-semibold text-gray-900 mt-1">{k.value}</div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Curated Playbooks */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">Lifecycle Playbooks</h3>
          <div className="text-xs text-gray-500">High‑impact actions by segment</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {bySeg.slice(0,5).map((s,i)=>(
            <div key={s.segment} className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 hover:shadow-md transition">
              <div className="absolute -top-10 -right-10 h-24 w-24 rounded-full opacity-20 blur-2xl"
                   style={{ background: `linear-gradient(135deg, ${SEGMENT_COLORS[s.segment]||'#93c5fd'} 0%, #ffffff 100%)` }} />
              <div className="relative z-10">
                <div className="text-sm font-semibold text-gray-900 mb-1">{s.segment}</div>
                <div className="text-xs text-gray-600 mb-3">Customers: {s.customers.toLocaleString('en-US')}</div>
                <ul className="space-y-1">
                  {playsForSegment(s.segment).slice(0,3).map((p)=> (
                    <li key={p} className="text-xs text-gray-700 flex items-center gap-1">
                      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{background: SEGMENT_COLORS[s.segment]||'#94a3b8'}} />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


